/**
 * Cart Drawer Upsell
 * Handles inline variant selection and add-to-cart for cart drawer upsells
 */

class CartDrawerUpsell extends HTMLElement {
  constructor() {
    super();
    this.mode = this.dataset.mode;
    this.variantData = null;
    this.selectedOptions = [];
    this.selectedVariant = null;
  }

  connectedCallback() {
    // Initialize based on mode
    if (this.mode === 'product_picker') {
      this.initProductPicker();
    } else if (this.mode === 'fixed_variant') {
      this.initFixedVariant();
    }
  }

  /**
   * Initialize Product Picker mode (inline variants)
   */
  initProductPicker() {
    // Get variant data from JSON
    this.variantData = this.getVariantData();
    if (!this.variantData) return;

    // Get initial selected options
    this.selectedOptions = this.getSelectedOptions();
    this.selectedVariant = this.getSelectedVariant();

    // Update UI based on initial variant
    this.updatePrice(this.selectedVariant);
    this.updateVariantText(this.selectedVariant);
    this.updateAvailability(this.selectedVariant);
    this.updateImage(this.selectedVariant);

    // Add event listeners for option changes
    this.addOptionListeners();

    // Add event listener for add to cart button
    const addButton = this.querySelector('[data-action="add-to-cart"]');
    if (addButton) {
      addButton.addEventListener('click', () => this.handleAddToCart());
    }
  }

  /**
   * Initialize Fixed Variant mode (one-click)
   */
  initFixedVariant() {
    const addButton = this.querySelector('[data-action="add-to-cart"]');
    if (addButton) {
      const variantId = addButton.dataset.variantId;
      addButton.addEventListener('click', () => this.handleAddToCart(variantId));
    }
  }

  /**
   * Get variant data from JSON script tag
   */
  getVariantData() {
    const dataEl = this.querySelector('[data-variant-json]');
    if (!dataEl) return null;

    try {
      return JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Failed to parse variant data:', e);
      return null;
    }
  }

  /**
   * Get currently selected options from pills/dropdowns
   */
  getSelectedOptions() {
    const options = [];
    const selector = this.querySelector('[data-upsell-selector]');
    if (!selector) return options;

    const pills = selector.querySelectorAll('[data-selector-type="pills"]');
    const dropdowns = selector.querySelectorAll('[data-selector-type="dropdown"]');

    // Get options from pills
    pills.forEach((pillGroup) => {
      const selectedPill = pillGroup.querySelector('.upsell-option-pill--selected');
      options.push(selectedPill ? selectedPill.dataset.optionValue : null);
    });

    // Get options from dropdowns
    dropdowns.forEach((select) => {
      options.push(select.value || null);
    });

    return options;
  }

  /**
   * Find variant matching selected options
   */
  getSelectedVariant() {
    if (!this.variantData) return null;

    // If no options selected (no selectors rendered), return first available variant
    if (!this.selectedOptions.length) {
      return this.variantData.variants.find((variant) => variant.available) || this.variantData.variants[0];
    }

    // Find variant matching selected options
    return this.variantData.variants.find((variant) => {
      return variant.options.every((value, index) => {
        return value === this.selectedOptions[index];
      });
    });
  }

  /**
   * Update variant text display (cart-item__variant-value elements)
   */
  updateVariantText(variant) {
    if (!variant) return;

    const variantElements = this.querySelectorAll('[data-variant-option]');
    variantElements.forEach((element, index) => {
      const valueSpan = element.querySelector('[data-variant-value]');
      if (valueSpan && variant.options[index]) {
        valueSpan.textContent = variant.options[index];
      }
    });
  }

  /**
   * Update price display based on selected variant
   */
  updatePrice(variant) {
    const priceEl = this.querySelector('[data-upsell-price]');
    if (!priceEl || !variant) return;

    // Use theme's money formatting if available
    const formattedPrice = this.formatMoney(variant.price);
    priceEl.textContent = formattedPrice;

    // Handle compare at price
    const parentCol = priceEl.closest('.cart-item__price');
    if (!parentCol) return;

    // Remove existing compare price if any
    const existingCompare = parentCol.querySelector('.struck-out-price');
    if (existingCompare) {
      existingCompare.remove();
    }

    // Add compare price if applicable
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const compareSpan = document.createElement('span');
      compareSpan.className = 'struck-out-price small-text';
      compareSpan.innerHTML = `<span class="theme-money">${this.formatMoney(variant.compare_at_price)}</span>`;
      parentCol.appendChild(compareSpan);
    }
  }

  /**
   * Update button availability based on selected variant
   */
  updateAvailability(variant) {
    const addButton = this.querySelector('[data-action="add-to-cart"]');
    if (!addButton) return;

    if (variant && variant.available) {
      addButton.disabled = false;
      addButton.classList.remove('btn--secondary');
      // Store original text if not already stored
      if (!addButton.dataset.originalText) {
        addButton.dataset.originalText = addButton.textContent;
      }
      addButton.textContent = addButton.dataset.originalText;
      // Update the variant ID on the button
      addButton.dataset.variantId = variant.id;
    } else {
      addButton.disabled = true;
      addButton.classList.add('btn--secondary');
      addButton.textContent = 'Unavailable';
    }
  }

  /**
   * Update product image based on selected variant
   */
  updateImage(variant) {
    if (!variant || !variant.featured_image) return;

    const imageEl = this.querySelector('.cart-item__image img');
    if (!imageEl) return;

    // Shopify image URL - already has width parameter
    const variantImageUrl = variant.featured_image;

    // Update src (handles both regular and lazy-loaded images)
    if (imageEl.dataset.manualSrc) {
      imageEl.dataset.manualSrc = variantImageUrl;
    } else {
      imageEl.src = variantImageUrl;
    }

    // Update srcset if it exists
    if (imageEl.srcset || imageEl.dataset.manualSrcset) {
      // Generate srcset with different widths for Shopify CDN
      const baseUrl = variantImageUrl.replace(/\?.*$/, ''); // Remove existing query params
      const srcsetValue = `${baseUrl}?width=100 100w, ${baseUrl}?width=200 200w`;

      if (imageEl.dataset.manualSrcset) {
        imageEl.dataset.manualSrcset = srcsetValue;
      } else {
        imageEl.srcset = srcsetValue;
      }
    }
  }

  /**
   * Add event listeners for option changes
   */
  addOptionListeners() {
    // Pill buttons
    const pills = this.querySelectorAll('.upsell-option-pill');
    pills.forEach((pill) => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        this.handlePillClick(pill);
      });
    });

    // Dropdown selects
    const selects = this.querySelectorAll('.upsell-option-select');
    selects.forEach((select) => {
      select.addEventListener('change', () => {
        this.handleOptionChange();
      });
    });
  }

  /**
   * Handle pill button click
   */
  handlePillClick(clickedPill) {
    const group = clickedPill.closest('[data-selector-type="pills"]');
    const pills = group.querySelectorAll('.upsell-option-pill');

    // Update selected state
    pills.forEach((pill) => {
      pill.classList.remove('upsell-option-pill--selected');
      pill.setAttribute('aria-pressed', 'false');
    });

    clickedPill.classList.add('upsell-option-pill--selected');
    clickedPill.setAttribute('aria-pressed', 'true');

    // Re-calculate variant
    this.handleOptionChange();
  }

  /**
   * Handle any option change (pill or dropdown)
   */
  handleOptionChange() {
    this.selectedOptions = this.getSelectedOptions();
    this.selectedVariant = this.getSelectedVariant();

    // Update UI
    this.updatePrice(this.selectedVariant);
    this.updateVariantText(this.selectedVariant);
    this.updateAvailability(this.selectedVariant);
    this.updateImage(this.selectedVariant);

    // Update pills availability based on which combinations are valid
    this.updateOptionAvailability();
  }

  /**
   * Update which option pills are available based on current selections
   */
  updateOptionAvailability() {
    const pillGroups = this.querySelectorAll('[data-selector-type="pills"]');

    pillGroups.forEach((group) => {
      const groupIndex = parseInt(group.dataset.optionPosition);
      const pills = group.querySelectorAll('.upsell-option-pill');

      pills.forEach((pill) => {
        const testOptions = [...this.selectedOptions];
        testOptions[groupIndex] = pill.dataset.optionValue;

        // Check if any variant matches these options
        const hasAvailableVariant = this.variantData.variants.some((variant) => {
          return variant.options.every((value, index) => value === testOptions[index]) && variant.available;
        });

        if (hasAvailableVariant) {
          pill.classList.remove('upsell-option-pill--disabled');
          pill.disabled = false;
        } else {
          pill.classList.add('upsell-option-pill--disabled');
          pill.disabled = true;
        }
      });
    });
  }

  /**
   * Handle add to cart action
   */
  async handleAddToCart(variantId = null) {
    const addButton = this.querySelector('[data-action="add-to-cart"]');
    if (!addButton) return;

    // Determine variant ID
    const id = variantId || addButton.dataset.variantId || (this.selectedVariant ? this.selectedVariant.id : null);
    if (!id) {
      console.error('No variant selected');
      return;
    }

    // Disable button and show loading state
    addButton.disabled = true;
    const originalText = addButton.textContent;
    addButton.textContent = 'Adding...';
    addButton.classList.add('loading');

    try {
      // Add to cart via AJAX
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: id,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Success - dispatch cart refresh event
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

      // Reset button
      addButton.classList.remove('loading');
      addButton.textContent = 'Added!';
      setTimeout(() => {
        addButton.textContent = originalText;
        addButton.disabled = false;
      }, 1500);

    } catch (error) {
      console.error('Error adding to cart:', error);

      // Show error state
      addButton.classList.remove('loading');
      addButton.textContent = 'Error - Try Again';
      setTimeout(() => {
        addButton.textContent = originalText;
        addButton.disabled = false;
      }, 2000);
    }
  }

  /**
   * Format money using theme's formatter if available
   */
  formatMoney(cents) {
    // Check if theme has money formatting utility
    if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.moneyFormat);
    }

    // Fallback: simple dollar formatting
    return '$' + (cents / 100).toFixed(2);
  }
}

// Register custom element
if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
